import { Injectable, inject } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { SystemService } from './system.service';
import { ISystemConfigModel } from './model/system-config.model';

/**
 * Per-client terminology override (LB audit): relabel the audit "Area" and "Workplace" wording to a
 * client-specific term (e.g. Area→"Project", Workplace→"Line"), per language, driven by
 * SystemConfiguration.clientConfig.areaLabel/workplaceLabel.
 *
 * Mechanism: after the system config is available (and on every language change) we merge overrides
 * into ngx-translate for the active language via translateService.setTranslation(lang, ..., true).
 * We do a case-preserving word replacement of the default term inside each targeted key's value, so
 * both bare labels ("Area") and phrases ("Select area", "Area Compliance", "Area / Workplace") are
 * relabelled, and plurals fall out naturally (Area→Project ⇒ Areas→Projects; Bereich→Projekt ⇒
 * Bereiche→Projekte). No component/template changes; a tenant without overrides is unaffected.
 */
@Injectable({ providedIn: 'root' })
export class TerminologyService {
  private translate = inject(TranslateService);
  private systemService = inject(SystemService);

  /** i18n key-paths whose text carries the audit "Area"/"Workplace" wording (Gemba + TPM mirror + shared). */
  private static readonly KEYS: string[] = [
    // shared
    'common.area', 'common.workplace',
    'my-actions.table.area', 'my-actions.detail.workplace',
    // gemba
    'gemba.common.labels.area', 'gemba.common.labels.workplace',
    'gemba.common.placeholders.select-area', 'gemba.common.placeholders.all-areas',
    'gemba.common.empty.no-area-data',
    'gemba.gemba-walk.table.area',
    'gemba.scheduler.dialog.area',
    'gemba.checklist.dropdowns.area', 'gemba.checklist.dropdowns.workplace',
    'gemba.audit-report.filters.areas', 'gemba.audit-report.panels.area-compliance',
    'gemba.status.table.area-workplace',
    'gemba.audit-checklist.detail.workplaces-column', 'gemba.audit-checklist.detail.workplaces-label',
    'gemba.audit-checklist.detail.workplaces-placeholder', 'gemba.audit-checklist.detail.edit-workplaces',
    // tpm (mirror of the gemba keys)
    'tpm.common.labels.area', 'tpm.common.labels.workplace',
    'tpm.common.placeholders.select-area', 'tpm.common.placeholders.all-areas',
    'tpm.common.empty.no-area-data',
    'tpm.scheduler.dialog.area',
    'tpm.checklist.dropdowns.area', 'tpm.checklist.dropdowns.workplace',
    'tpm.audit-report.filters.areas', 'tpm.audit-report.panels.area-compliance',
    'tpm.status.table.area-workplace',
    'tpm.audit-checklist.detail.workplaces-column', 'tpm.audit-checklist.detail.workplaces-label',
    'tpm.audit-checklist.detail.workplaces-placeholder', 'tpm.audit-checklist.detail.edit-workplaces',
  ];

  /** Default singular word per lang, captured once BEFORE overriding, e.g. { en: {area:'Area', workplace:'Workplace'} }. */
  private defaults = new Map<string, { area: string; workplace: string }>();
  private config: ISystemConfigModel | null = null;
  private started = false;

  /** Call once at app start. Re-applies whenever the config loads or the language changes. */
  init(): void {
    if (this.started) { return; }
    this.started = true;
    // Subscribe PASSIVELY to the system config — do NOT call getSystemState(), which lazily fires
    // GET /api/system/config on first subscribe. This service inits from App.ngOnInit (outside
    // MsalGuard), so on MSAL that request would run BEFORE login, and the MsalInterceptor would
    // bounce the un-authenticated call into an infinite Microsoft redirect loop. The config is
    // loaded by the normal auth-gated flow (App for LDAP, AppMainComponent post-login for MSAL);
    // terminology only needs to react once it arrives.
    this.systemService.systemState
      .pipe(filter((cfg): cfg is ISystemConfigModel => cfg != null))
      .subscribe(cfg => {
        this.config = cfg;
        this.apply(this.translate.currentLang);
      });
    this.translate.onLangChange.subscribe((e: LangChangeEvent) => this.apply(e.lang));
  }

  private apply(lang: string | undefined): void {
    if (!lang) { return; }
    const areaTerm = this.config?.clientConfig?.areaLabel?.[lang];
    const workplaceTerm = this.config?.clientConfig?.workplaceLabel?.[lang];
    if (!areaTerm && !workplaceTerm) { return; } // default tenant → leave the standard translations

    // Bail until the language JSON is actually loaded: instant() returns the key itself when a
    // translation is missing. Applying now would (a) do nothing and (b) poison the default-word cache
    // with the key string. onLangChange re-triggers apply() once the JSON has loaded.
    const defArea = this.translate.instant('common.area');
    const defWorkplace = this.translate.instant('common.workplace');
    if (defArea === 'common.area' || defWorkplace === 'common.workplace') { return; }

    // Capture the default singular words for this language ONCE (i.e. before we override them).
    if (!this.defaults.has(lang)) {
      this.defaults.set(lang, { area: defArea, workplace: defWorkplace });
    }
    const def = this.defaults.get(lang)!;

    const overrides: Record<string, any> = {};
    let changed = false;
    for (const key of TerminologyService.KEYS) {
      let value = this.translate.instant(key);
      if (typeof value !== 'string' || value === key) { continue; } // missing key → skip
      if (areaTerm && def.area) { value = this.replaceWord(value, def.area, areaTerm); }
      if (workplaceTerm && def.workplace) { value = this.replaceWord(value, def.workplace, workplaceTerm); }
      this.setPath(overrides, key, value);
      changed = true;
    }
    if (changed) { this.translate.setTranslation(lang, overrides, true); }
  }

  /** Case-insensitive, case-preserving replacement of `from` with `to` (plurals handled via substring). */
  private replaceWord(text: string, from: string, to: string): string {
    if (!from || !to) { return text; }
    const re = new RegExp(this.escapeRegExp(from), 'gi');
    return text.replace(re, match => this.matchCase(match, to));
  }

  private matchCase(match: string, replacement: string): string {
    const upper = match.charAt(0) === match.charAt(0).toUpperCase();
    return (upper ? replacement.charAt(0).toUpperCase() : replacement.charAt(0).toLowerCase()) + replacement.slice(1);
  }

  private escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private setPath(obj: Record<string, any>, path: string, value: string): void {
    const parts = path.split('.');
    let cur: Record<string, any> = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = cur[parts[i]] || {};
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
  }
}
